import cn from 'classnames';
import toast from 'react-hot-toast';
import styles from './SubscriptionForm.module.scss';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { fetchData } from '@/services/fetchData';
import { FETCH_METHODS } from '@/types';
import { Input } from '../Input';
import { Checkbox } from '../Checkbox';
import Link from 'next/link';
import { Button } from '../Button';
import { EMAIL_REGEX } from '@/constants';
import { useEffect, useState } from 'react';
import { parseText } from '@/utils/parseText';

type SubscriptionFormProps = {
  className?: string;
  translations: Record<string, string>;
  locale?: string;
};

type SubscriptionFormInputs = {
  name: string;
  email: string;
  agreeToProcessingPersonalData: boolean;
};

export function SubscriptionForm({
  className,
  translations,
  locale,
}: SubscriptionFormProps) {
  const wrapperClassName = cn(styles.wrapper, className);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
  } = useForm<SubscriptionFormInputs>({
    defaultValues: {
      agreeToProcessingPersonalData: false,
    },
  });

  const formFields = useWatch({ control });

  const onSubmit: SubmitHandler<SubscriptionFormInputs> = async (data) => {
    const response = await fetchData({
      url: `/order/content/subscribe`,
      method: FETCH_METHODS.POST,
      body: data,
    });

    if (response && response.code === 200) {
      toast.success(translations.successSubscribe);
      reset();
    }

    if ((response && response.code === 400) || !response) {
      toast.error(translations.errorSubscribe);
    }
  };

  const [policyLink, setPolicyLink] = useState('');

  useEffect(() => {
    const fetchPolicyLink = async () => {
      const response = await fetchData({
        url: `/settings/view`,
        locale,
      });
      setPolicyLink(response.linkProcessingPersonalData || '/faq');
    };
    fetchPolicyLink();
  }, []);

  const titleHTML = { __html: translations.subscriptionTitle };

  return (
    <form className={wrapperClassName} onSubmit={handleSubmit(onSubmit)}>
      <h4 className={styles.title} dangerouslySetInnerHTML={titleHTML} />
      <p className={styles.subtitle}>{translations.subscriptionSubtitle}</p>
      <div className={styles.inputWrapper}>
        <Input
          register={register}
          label={translations.name}
          name="name"
          required
          placeholder={translations.name}
          pattern={/^[a-zа-я ,.'-]+$/i}
          setValue={setValue}
          error={errors.name}
        />
      </div>
      <div className={styles.inputWrapper}>
        <Input
          register={register}
          label={translations.email}
          name="email"
          pattern={EMAIL_REGEX}
          required
          placeholder="mail@mail.ru"
          setValue={setValue}
          error={errors.email}
        />
      </div>
      <div className={styles.checkboxWrapper}>
        <Checkbox
          register={register}
          label={
            <>
              {parseText(translations.agreeTo)}
              <Link
                className={styles.highlight}
                href={policyLink}
                target="_blank"
              >
                {parseText(translations.personalData)}
              </Link>
            </>
          }
          name="agreeToProcessingPersonalData"
          required
        />
        {errors.agreeToProcessingPersonalData && (
          <div className={styles.error}>{translations.required}</div>
        )}
      </div>
      <Button
        text={translations.subscribe}
        type="submit"
        onClick={() => {}}
        className={styles.button}
        disabled={!formFields.agreeToProcessingPersonalData}
      />
    </form>
  );
}
